<template>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class="text-[#414141] font-sans">
            <template v-if="isLoading">
                <div class="w-full h-full flex items-center justify-center">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00423D]"></div>
                </div>
            </template>
            <template v-else>
                <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">Development Documents</h3>
                <p class="text-[14px] leading-[22px] mb-[1rem]">Below is a list of standard development documents that are used to solidify a gift between a donor and an organization. Select the document templates(s) your organization would like to customize:</p>

                <!-- Development Documents Container -->
                <div class="mt-6">
                    <!-- Individual Documents -->
                    <div v-for="(doc, idx) in developmentDocs" :key="idx" class="mb-6 ml-4">
                        <div class="flex items-start">
                            <input
                                type="checkbox"
                                v-model="doc.isSelected"
                                class="form-checkbox h-4 w-4 mt-1 cursor-pointer"
                            />
                            <div class="ml-2">
                                <span 
                                    class="text-[#00423D] font-semibold uppercase block cursor-pointer"
                                    @click="doc.isSelected = !doc.isSelected"
                                >{{ doc.name }}</span>
                                <p class="text-[14px] leading-[22px] text-[#414141] mt-2">{{ doc.description }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Other Documents Section -->
                <h3 class="text-[1.25rem] leading-[1.75rem] mt-8 mb-[1rem] font-semibold">Other Documents</h3>
                <div class="mt-6">
                    <div v-for="(doc, idx) in otherDocs" :key="idx" class="mb-6 ml-4">
                        <div class="flex items-start">
                            <input
                                type="checkbox"
                                v-model="doc.isSelected"
                                class="form-checkbox h-4 w-4 mt-1 cursor-pointer"
                            />
                            <div class="ml-2">
                                <span 
                                    class="text-[#00423D] font-semibold uppercase block cursor-pointer"
                                    @click="doc.isSelected = !doc.isSelected"
                                >{{ doc.name }}</span>
                                <p class="text-[14px] leading-[22px] text-[#414141] mt-2">{{ doc.description }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStepValidation } from '@/composables/useStepValidation';
import { useAuthStore } from "@/stores/auth";
import { useUserProjectsStore } from "@/stores/projects";
import global from "@/global";
import { useDevelopmentDocsStore } from '@/stores/developmentDocs';

// Store instances
const authStore = useAuthStore();
const projectsStore = useUserProjectsStore();
const devDocsStore = useDevelopmentDocsStore();

// State
const isLoading = ref(true);
const developmentDocs = ref([
    {
        identity: "pledge_form",
        name: "pledge form",
        description: "This is a simple contract for organizations to use when donors are making pledges to support the organization for a future gift and/or a gift over a period of years. Generally used for gifts less than $100,000 and that already have allocation codes setup within the organization's database",
        isSelected: false,
        url: "/files/PledgeForm.docx",
        fileName: "Pledge Form"
    },
    {
        identity: "gift_agreement_for_an_outright_gift",
        name: "gift agreement for an outright gift",
        description: "This is a customized contract that is used when a one-time gift, not a pledge, is being made by the donor to an organization. Generally used for gifts of $100,000 and/or when a new allocation code is being created within an organization.",
        isSelected: false,
        url: "/files/GiftAgreementForAnOutrightGift.docx",
        fileName: "Gift Agreement For an Outright Gift"
    },
    {
        identity: "gift_agreement_for_naming_an_endowment",
        name: "gift agreement for naming an endowment",
        description: "This is a customized contract that is used when a pledge, over a period of years, is being made by the donor to an organization. Generally used for gifts of $100,000 and/or when a new allocation code is being created within an organization.",
        isSelected: false,
        url: "/files/GiftAgreementForNamingAnEndowment.docx",
        fileName: "Gift Agreement For Naming an Endowment"
    }
]);

const otherDocs = ref([
    {
        identity: "donor_acknowledgment_letter",
        name: "donor acknowledgment letter",
        description: "This is a sample letter that organizations can use to thank donors for their support. Be sure to customize this letter with your organization's language",
        isSelected: false,
        url: "/files/DonorAcknowledgementLetter.docx",
        fileName: "Donor Acknowledgement Letter"
    },
    {
        identity: "stock_and_wire_transfer_instructions",
        name: "stock and wire transfer instructions",
        description: "Standard form that documents how an organization accepts stock or wire transfers from donors. Many donors prefer to make stock or wire transfers for their gifts. Having this information prepared upfront will help organizations collect gifts and pledges more quickly.",
        isSelected: false,
        url: "/files/StockAndWireTransferInstructions.docx",
        fileName: "Stock and Wire Transfer Instructions"
    }
]);

// API Integration
async function fetchDocuments() {
    try {
        const res = await fetch(`${global.API_URL}/policies-and-procedures`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
            },
        });
        
        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }
        
        const data = await res.json();
        if (data.development) {
            developmentDocs.value.forEach(doc => {
                doc.isSelected = data.development[doc.identity] || false;
            });
        }
        if (data.other) {
            otherDocs.value.forEach(doc => {
                doc.isSelected = data.other[doc.identity] || false;
            });
        }
    } catch (error) {
        console.error("Failed to fetch documents:", error);
    }
}

async function saveDocuments() {
    try {
        const docsToSave = {};
        
        developmentDocs.value.forEach(doc => {
            docsToSave[doc.identity] = doc.isSelected;
        });
        
        otherDocs.value.forEach(doc => {
            docsToSave[doc.identity] = doc.isSelected;
        });

        const res = await fetch(`${global.API_URL}/policies-and-procedures`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(docsToSave),
        });
        
        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }
        
        const data = await res.json();
        if (!data.status) {
            throw new Error('Failed to save documents');
        }
        
        return true;
    } catch (error) {
        console.error("Failed to save documents:", error);
        return false;
    }
}

async function downloadSelectedDocuments() {
    const allDocs = [...developmentDocs.value, ...otherDocs.value];
    
    // Download selected documents
    allDocs.forEach(doc => {
        if (doc.isSelected && doc.url) {
            const link = document.createElement('a');
            link.href = doc.url;
            link.download = doc.fileName;
            link.click();
        }
    });

    // Call the increment download flag API
    try {
        const res = await fetch(`${global.API_URL}/policies_and_procedures/increment_download_flag`, {
            method: 'PATCH',
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
            },
        });

        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }
    } catch (error) {
        console.error('Failed to update download flag:', error);
    }
}

// Use the validation composable with custom validation
useStepValidation({
    validateFn: async () => {
        // First save the documents
        const saveResult = await saveDocuments();
        if (!saveResult) return false;

        // If save was successful, download selected documents
        await downloadSelectedDocuments();
        
        // Store selected document names in Pinia store for StepsView.vue to access
        const selectedDocs = [
          ...developmentDocs.value.filter(doc => doc.isSelected).map(doc => doc.name),
          ...otherDocs.value.filter(doc => doc.isSelected).map(doc => doc.name)
        ];
        devDocsStore.setSelectedDocs(selectedDocs);
        
        // Notify the store that data has been updated
        projectsStore.setQuestionUpdate();
        
        return true;
    }
});

onMounted(async () => {
    await fetchDocuments();
    isLoading.value = false;
});
</script>

<style scoped>
.form-checkbox {
    @apply rounded;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 16px;
    height: 16px;
    border: 2px solid #00423D;
    border-radius: 4px;
    background-color: white;
    cursor: pointer;
    position: relative;
    flex-shrink: 0;
}

.form-checkbox:checked {
    background-color: #00423D;
    border-color: #00423D;
}

.form-checkbox:checked::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}


/* Loading animation */
@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.animate-spin {
    animation: spin 1s linear infinite;
}
</style> 